import React, { createContext, useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const navigate = useNavigate();

  // Define searchable items with their routes and metadata
  const searchableItems = useMemo(() => [
    // Dashboard & Analytics
    { id: 'dashboard', title: 'Dashboard', route: '/dashboard', category: 'Navigation', keywords: ['overview', 'stats', 'analytics', 'metrics'] },
    { id: 'reports', title: 'Reports', route: '/reports', category: 'Analytics', keywords: ['analytics', 'data', 'export', 'charts'] },
    { id: 'storage', title: 'Storage Analytics', route: '/storage', category: 'Analytics', keywords: ['storage', 'usage', 'files', 'quota', 'disk'] },
    
    // User Management
    { id: 'users', title: 'Users', route: '/users', category: 'Management', keywords: ['users', 'accounts', 'members', 'people'] },
    { id: 'teams', title: 'Teams', route: '/teams', category: 'Management', keywords: ['teams', 'groups', 'organization', 'departments'] },
    { id: 'roles', title: 'Role Management', route: '/roles', category: 'Security', keywords: ['roles', 'permissions', 'access', 'privileges'] },
    
    // Domain & Email
    { id: 'domains', title: 'Domains', route: '/domains', category: 'Email', keywords: ['domains', 'dns', 'verification', 'email'] },
    { id: 'migration', title: 'Email Migration', route: '/email-migration', category: 'Email', keywords: ['migration', 'import', 'transfer', 'email'] },
    
    // System
    { id: 'settings', title: 'Settings', route: '/settings', category: 'System', keywords: ['settings', 'configuration', 'preferences'] },
    { id: 'support', title: 'Support Center', route: '/support', category: 'Help', keywords: ['support', 'help', 'tickets', 'assistance', 'contact'] },
    
    // Actions
    { id: 'add-user', title: 'Add New User', route: '/users', category: 'Actions', keywords: ['add user', 'create user', 'new user', 'invite'] },
    { id: 'add-team', title: 'Create Team', route: '/teams', category: 'Actions', keywords: ['add team', 'create team', 'new team'] },
    { id: 'add-domain', title: 'Add Domain', route: '/domains', category: 'Actions', keywords: ['add domain', 'new domain', 'register'] },
    { id: 'export-data', title: 'Export Data', route: '/reports', category: 'Actions', keywords: ['export', 'download', 'backup'] },
    
    // Quick Stats (Dashboard sections)
    { id: 'user-stats', title: 'User Statistics', route: '/dashboard', category: 'Quick Access', keywords: ['user count', 'active users', 'user growth'] },
    { id: 'storage-stats', title: 'Storage Usage', route: '/storage', category: 'Quick Access', keywords: ['storage used', 'disk space', 'quota'] },
    { id: 'email-stats', title: 'Email Statistics', route: '/reports', category: 'Quick Access', keywords: ['email volume', 'message count'] },
    
    // Settings sections
    { id: 'business-profile', title: 'Business Profile', route: '/settings', category: 'Profile', keywords: ['company', 'business info', 'profile'] },
    { id: 'security-settings', title: 'Security Settings', route: '/settings', category: 'Security', keywords: ['security', 'password', 'mfa', '2fa'] },
    { id: 'notification-settings', title: 'Notifications', route: '/settings', category: 'Preferences', keywords: ['notifications', 'alerts', 'emails'] },
  ], []);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    return searchableItems
      .filter(item => {
        const titleMatch = item.title.toLowerCase().includes(query);
        const keywordMatch = item.keywords.some(keyword => keyword.toLowerCase().includes(query));
        const categoryMatch = item.category.toLowerCase().includes(query);
        
        return titleMatch || keywordMatch || categoryMatch;
      })
      .slice(0, 8) // Limit results
      .sort((a, b) => {
        // Prioritize exact title matches
        const aExactTitle = a.title.toLowerCase().includes(query);
        const bExactTitle = b.title.toLowerCase().includes(query);
        
        if (aExactTitle && !bExactTitle) return -1;
        if (!aExactTitle && bExactTitle) return 1;
        
        return 0;
      });
  }, [searchQuery, searchableItems]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      setIsSearchOpen(true);
    } else {
      setIsSearchOpen(false);
    }
  };

  const handleSelectItem = (item) => {
    navigate(item.route);
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  const value = {
    searchQuery,
    searchResults,
    isSearchOpen,
    handleSearch,
    handleSelectItem,
    clearSearch,
    setIsSearchOpen,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};